<template>
  <div v-if="visible">
    <b-container fluid class="geofence-edit p-0 m-0 p-relative h-100">
      <b-card v-if="filtersOpen" style="z-index: 20">
        <b-row>
          <b-col>
            <div class="d-flex">
              <b-button size="sm" :variant="!acGeofence ? 'primary' : 'dark'" @click="acGeofence = !acGeofence" :title="acGeofence ? $gettext('Close Autocomplete') : $gettext('Search for Existing GeoFences')" v-b-tooltip.hover>
                <span v-if="!acGeofence">
                  <font-awesome-icon icon="search" />
                  <translate>GeoFences</translate>
                </span>
                <span v-else>✕</span>
              </b-button>
              <div class="ml-1" style="flex-grow: 1" v-if="acGeofence">
                <geofence-autocomplete @selected="centerElement($event)" class="w-100" />
              </div>
            </div>
          </b-col>
          <b-col>
            <div class="d-flex">
              <b-button size="sm" :variant="!acGeoline ? 'primary' : 'dark'" @click="acGeoline = !acGeoline" :title="acGeoline ? $gettext('Close Autocomplete') : $gettext('Search for Existing GeoLines')" v-b-tooltip.hover>
                <span v-if="!acGeoline">
                  <font-awesome-icon icon="search" />
                  <translate>GeoLines</translate>
                </span>
                <span v-else>✕</span>
              </b-button>
              <div class="ml-1" style="flex-grow: 1" v-if="acGeoline">
                <geoline-autocomplete @selected="centerElement($event)" class="w-100" />
              </div>
            </div>
          </b-col>
          <b-col>
            <div class="d-flex">
              <b-button size="sm" :variant="!acGeopoint ? 'primary' : 'dark'" @click="acGeopoint = !acGeopoint" :title="acGeopoint ? $gettext('Close Autocomplete') : $gettext('Search for Existing GeoPoints')" v-b-tooltip.hover>
                <span v-if="!acGeopoint">
                  <font-awesome-icon icon="search" />
                  <translate>GeoPoints</translate>
                </span>
                <span v-else>✕</span>
              </b-button>
              <div class="ml-1" style="flex-grow: 1" v-if="acGeopoint">
                <geopoint-autocomplete @selected="centerElement($event)" class="w-100" />
              </div>
            </div>
          </b-col>
        </b-row>
      </b-card>
      <editable-map
        editable
        ref="map"
        id="map"
        :zoom.sync="currentZoom"
        :options="mapOptions"
        :center="center"
        :min-zoom="minZoom"
        :max-zoom="maxZoom"
        :bounds.sync="bounds"
        :style="'z-index: 19; height: ' + computedMapHeight"
        @ready="emitReady"
      >
        <l-tile-layer
          v-for="tileProvider in computedTileProviders"
          :key="tileProvider.name"
          :name="tileProvider.name"
          :visible="tileProvider.visible"
          :url="tileProvider.url"
          :attribution="tileProvider.attribution"
          layer-type="base"
          :options="tileProvider.options"
          v-if="tileProvidersReady"
        />
        <l-control-fullscreen position="topleft"
          :options="{title: { false: $gettext('Go fullscreen'), true: $gettext('Back to normal') }}"
        />
        <l-control-layers
          :position="layersPosition"
          :collapsed="false"
          :sort-layers="true"
        />
        <l-control-zoom :position="zoomPosition" />
        <l-control-attribution
          :position="attributionPosition"
          prefix="Moova"
        />
        <l-control-scale position="bottomright" :imperial="imperial" :metric="metric" />

        <l-control position="bottomleft">
          <div class="map-filters" v-if="computedGeoTypesSelect.length > 0">
            <b-card class="mb-0 p-1">
              <strong>
                <translate>Show Nearby Elements</translate>
                <strong class="text-danger" v-if="nearbyLayersSwitch && currentZoom <= 12" :title="$gettext('Zoom-in to Search Again')" v-b-tooltip.hover>(!)</strong>
              </strong>
              <indeterminate-checkboxes all-selected :parent-title="$gettext('GeoFences') + ` (${geoFences.length})`" :parent-title-all-selected="$gettext('GeoFences') + ` (${geoFences.length})`" v-model="polygonTypes" :child-options="computedGeoTypesSelect.filter(x => x.type === 'polygon')" />
              <indeterminate-checkboxes all-selected :parent-title="$gettext('GeoLines') + ` (${geoLines.length})`" :parent-title-all-selected="$gettext('GeoLines') + ` (${geoLines.length})`" v-model="lineTypes" :child-options="computedGeoTypesSelect.filter(x => x.type === 'linestring')" />
              <indeterminate-checkboxes all-selected :parent-title="$gettext('GeoPoints') + ` (${geoPoints.length})`" :parent-title-all-selected="$gettext('GeoPoints') + ` (${geoPoints.length})`" v-model="pointTypes" :child-options="computedGeoTypesSelect.filter(x => x.type === 'point')" />
            </b-card>
          </div>
        </l-control>

        <l-feature-group ref="nearbyLayers" id="nearbyLayers" :visible="nearbyLayersSwitch">
          <editable-polygon
            v-for="(geofence, index) in geoFences"
            :key="'notLinkedGeofence' + index"
            v-if="geoFences.length > 0 && geofence.coordinates"
            :lat-lngs="geofence.coordinates"
            :editable="geofence.editable"
            :visible="polygonTypes.length > 0"
            :color="stringToColour('11' + geofence.geo_type.name)"
            :fillColor="stringToColour('11' + geofence.geo_type.name)"
            :weight="2"
            @editable:editing="updateFenceEdited($event, index)"
          >
            <l-popup :options="{closeButton: false}">
              <b-button v-if="!geofence.editable" @click="geoFences[index].editable = true; editing = true; map.closePopup();" variant="dark" :title="$gettext('Edit this GeoFence Polygon')" v-b-tooltip.hover><font-awesome-icon icon="draw-polygon" /></b-button>
              <b-button v-if="!geofence.editable" @click="map.closePopup();" :to="{name: 'GeofencesEdit', params: { id: geofence.id }}" target="_blank" variant="dark" :title="$gettext('Edit this GeoFence Data and Relations')" v-b-tooltip.hover><font-awesome-icon icon="edit" /></b-button>
              <b-button v-if="!geofence.editable" @click="deleteGeoFence(geoFences[index]); map.closePopup();" variant="danger" :title="$gettext('Completely DELETE this GeoFence and its Relations')" v-b-tooltip.hover><font-awesome-icon icon="trash-alt" /></b-button>
              <b-button v-if="geofence.editable" @click="saveGeoFence($event, geofence); map.closePopup();" variant="success" :title="$gettext('Save Changes')" v-b-tooltip.hover><font-awesome-icon icon="check" /></b-button>
              <b-button v-if="geofence.editable" @click="geoFences[index].editable = false; getNearbyElements(); map.closePopup();" variant="danger" :title="$gettext('Cancel Changes')" v-b-tooltip.hover><font-awesome-icon icon="ban" /></b-button>
            </l-popup>
            <l-tooltip>
              <geofence-title :geofence="geofence" />
            </l-tooltip>
          </editable-polygon>

          <editable-polyline
            v-for="(line, index) in geoLines"
            :key="'notLinkedLine' + index"
            :lat-lngs="line.coordinates"
            :editable="line.editable"
            :visible="lineTypes.length > 0"
            :color="line.geo_type.name.indexOf('entry') !== -1 ? '#00cc00' : (line.geo_type.name.indexOf('exit') !== -1 ? '#cc0000' : '#000')"
            :weight="2"
            @editable:editing="updateLine($event, index)"
          >
            <l-popup :options="{closeButton: false}">
              <b-button v-if="!line.editable" @click="geoLines[index].editable = true; editing = true; map.closePopup();" variant="dark" :title="$gettext('Edit this GeoLine')" v-b-tooltip.hover><font-awesome-icon icon="edit" /></b-button>
              <b-button v-if="!line.editable" @click="deleteGeoLine(geoLines[index]); map.closePopup();" variant="danger" :title="$gettext('Completely DELETE this GeoLine and its Relations')" v-b-tooltip.hover><font-awesome-icon icon="trash-alt" /></b-button>
              <b-button v-if="line.editable" @click="saveGeoLine($event, line); map.closePopup();" variant="success" :title="$gettext('Save Changes')" v-b-tooltip.hover><font-awesome-icon icon="check" /></b-button>
              <b-button v-if="line.editable" @click="geoLines[index].editable = false; getNearbyElements(); map.closePopup();" variant="danger" :title="$gettext('Cancel Changes')" v-b-tooltip.hover><font-awesome-icon icon="ban" /></b-button>
            </l-popup>
            <l-tooltip>
              {{line.name}}
            </l-tooltip>
          </editable-polyline>

          <l-marker :lat-lng="point" v-for="(point, index) in geoPoints" :key="'notLinkedPoint' + index" :draggable="point.editable" @update:latLng="updatePoint($event, index)" :visible="pointTypes.length > 0">
            <l-icon
              :icon-anchor="[9, 24]"
              style="position: relative;"
              :tooltipAnchor="[9, -12]"
              :icon-size="[18, 24]"
              >
            <font-awesome-icon icon="map-marker" size="2x" style="filter: drop-shadow(0 0 1px rgba(255,255,255,1)) drop-shadow(0 2px 2px rgba(0,0,0,.3));" :color="point.geo_type.name.indexOf('entry') !== -1 ? '#00cc00' : (point.geo_type.name.indexOf('exit') !== -1 ? '#cc0000' : '#000')" />
            </l-icon>
            <l-popup :options="{closeButton: false}">
              <b-button v-if="!point.editable" @click="geoPoints[index].editable = true; editing = true; map.closePopup();" variant="dark" :title="$gettext('Edit this GeoPoint')" v-b-tooltip.hover><font-awesome-icon icon="edit" /></b-button>
              <b-button v-if="!point.editable" @click="deleteGeoPoint(geoPoints[index]); map.closePopup();" variant="danger" :title="$gettext('Completely DELETE this GeoPoint and its Relations')" v-b-tooltip.hover><font-awesome-icon icon="trash-alt" /></b-button>
              <b-button v-if="point.editable" @click="saveGeoPoint($event, point); map.closePopup();" variant="success" :title="$gettext('Save Changes')" v-b-tooltip.hover><font-awesome-icon icon="check" /></b-button>
              <b-button v-if="point.editable" @click="geoPoints[index].editable = false; getNearbyElements(); map.closePopup();" variant="danger" :title="$gettext('Cancel Changes')" v-b-tooltip.hover><font-awesome-icon icon="ban" /></b-button>
            </l-popup>
            <l-tooltip>
              {{point.name}}
            </l-tooltip>
          </l-marker>
        </l-feature-group>
      </editable-map>
    </b-container>
  </div>
</template>

<script>
import {
  GeofenceService,
  GeoLineService,
  GeoPointService
} from '@/services/api'
import IndeterminateCheckboxes from '@/components/IndeterminateCheckboxes'
import GeofenceAutocomplete from '@/components/Autocompletes/GeofenceAutocomplete'
import GeolineAutocomplete from '@/components/Autocompletes/GeolineAutocomplete'
import GeopointAutocomplete from '@/components/Autocompletes/GeopointAutocomplete'
import L from 'leaflet'
import { EditableMap, EditablePolygon, EditablePolyline } from 'vue2-leaflet-editable'
import {
  LTileLayer,
  LFeatureGroup,
  LMarker,
  LTooltip,
  LPopup,
  LControlZoom,
  LControlAttribution,
  LControlScale,
  LControlLayers,
  LControl,
  LIcon
} from 'vue2-leaflet'
import LControlFullscreen from 'vue2-leaflet-fullscreen'

export default {
  name: 'GeofenceGeneralMap',
  components: {
    IndeterminateCheckboxes,
    GeofenceAutocomplete,
    GeolineAutocomplete,
    GeopointAutocomplete,
    EditableMap,
    EditablePolygon,
    EditablePolyline,
    LTileLayer,
    LFeatureGroup,
    LMarker,
    LTooltip,
    LPopup,
    LControlZoom,
    LControlAttribution,
    LControlScale,
    LControlLayers,
    LControl,
    LIcon,
    LControlFullscreen
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    filtersOpen: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      geoFences: [],
      geoLines: [],
      geoPoints: [],
      map: null,
      mapReady: false,
      center: [-34.6158036, -58.5035093],
      bounds: null,
      opacity: 1,
      mapOptions: {
        zoomControl: false,
        attributionControl: false,
        zoomSnap: true
      },
      currentZoom: 5,
      minZoom: 1,
      maxZoom: 18,
      zoomPosition: 'topleft',
      layersPosition: 'topright',
      attributionPosition: 'bottomright',
      imperial: false,
      metric: false,
      tileProvidersReady: false,
      tileProvidersLocal: [],
      nearbyLayers: null,
      nearbyLayersSwitch: true,
      polygonTypes: [],
      lineTypes: [],
      pointTypes: [],
      zoom: 13,
      searching: {
        geoFences: false,
        geoLines: false,
        geoPoints: false
      },
      acGeofence: false,
      acGeoline: false,
      acGeopoint: false,
      computedMapHeight: '500px',
      editing: false
    }
  },
  computed: {
    computedGeoTypesSelect() {
      return this.geoTypes.map(x => {
        return {
          text: this.toTitleCase(x.name),
          value: x.id,
          type: x.type
        }
      })
    },
    computedTileProviders() {
      return this.setDefaultTileProvider()
    }
  },
  watch: {
    tileProviders: {
      handler(newVal, oldVal) {
        this.tileProvidersLocal = JSON.parse(JSON.stringify(newVal))
        console.log(this.tileProvidersLocal)
        this.setDefaultTileProvider()
      },
      deep: true
    },
    bounds: {
      handler(newVal, oldVal) {
        this.map.closePopup()
        this.getNearbyElements()
      },
      deep: true
    },
    polygonTypes(val) {
      this.getNearbyElements()
    },
    lineTypes(val) {
      this.getNearbyElements()
    },
    pointTypes(val) {
      this.getNearbyElements()
    },
    visible(val) {
      if (val) {
        this.mapHeight()
      } else {
        this.geoFences = []
        this.geoLines = []
        this.geoPoints = []
      }
    },
    filtersOpen(val) {
      this.$nextTick(() => { this.computedMapHeight = this.mapHeight() })
    }
  },
  async mounted() {
    this.imperial = this.frontConfig?.i18n?.unitSystem === 'imperial'
    this.metric = !this.imperial
    await this.checkMapProviders()
    this.currentZoom = this.zoom
    this.tileProvidersLocal = JSON.parse(JSON.stringify(this.tileProviders))
    this.center = [38.129347, -121.208467] // LODI FIXME
  },
  beforeDestroy() {
    this.geoFences = []
    this.geoLines = []
    this.geoPoints = []
  },
  methods: {
    setFenceCoordinates(geofence) {
      // console.log('setFenceCoordinates > ', geofence)

      // Backup
      geofence.originalFence = !geofence.originalFence ? Object.assign({}, geofence.fence) : geofence.originalFence

      // For map
      let cleanupDupes = geofence.fence && geofence.fence.coordinates ? [Array.from(new Set(geofence.fence.coordinates[0].map(a => a.join('|'))), s => s.split('|').map(Number))] : null
      geofence.coordinates = cleanupDupes ? cleanupDupes.map(x =>
        x.map(([lng, lat]) => ({lat, lng}))
      ) : geofence.coordinates

      // To save
      geofence.fence = cleanupDupes ? cleanupDupes.map(x => x.map(y => [y[1], y[0]]))[0] : geofence.fence
      return geofence
    },
    setLineCoordinates(line) {
      // console.log('setLineCoordinates > ', line)

      // Backup
      line.originalPath = !line.originalPath ? Object.assign({}, line.path) : line.originalPath

      // For map
      let cleanupDupes = line.path && line.path.coordinates ? Array.from(new Set(line.path.coordinates.map(a => a.join('|'))), s => s.split('|').map(Number)) : null
      line.coordinates = cleanupDupes ? cleanupDupes.map(([lng, lat]) => ({lat, lng})) : line.coordinates

      // To save
      line.path = cleanupDupes ? cleanupDupes.map(x => [x[1], x[0]]) : line.path
      return line
    },
    setPointCoordinates(point) {
      return point
    },
    async getNearbyElements() {
      if (this.nearbyLayersSwitch && this.map && this.map.getZoom() > 12 && !this.editing) {
        if (this.polygonTypes.length > 0) {
          if (!this.searching.geoFences) {
            this.searching.geoFences = true
            let params = {
              includes: [
                'geo_type'
              ],
              filter_groups: [
                {
                  filters: [
                    ['geo_type_id', 'in', this.polygonTypes],
                    ['boundary', 'eq', this.bounds._southWest.lat + ',' + this.bounds._southWest.lng + '|' + this.bounds._northEast.lat + ',' + this.bounds._northEast.lng]
                  ]
                }
              ]
            }
            GeofenceService.getAll({params})
              .then(response => {
                this.geoFences = []
                this.$nextTick(() => {
                  this.geoFences = response.body.data.map(x => { return {...this.setFenceCoordinates(x), editable: false} })
                  this.searching.geoFences = false
                })
              })
          }
        } else {
          this.geoFences = []
          this.searching.geoFences = false
        }
        if (this.lineTypes.length > 0) {
          if (!this.searching.geoLines) {
            this.searching.geoLines = true
            let params = {
              includes: [
                'geo_type'
              ],
              filter_groups: [
                {
                  filters: [
                    ['geo_type_id', 'in', this.lineTypes],
                    ['boundary', 'eq', this.bounds._southWest.lat + ',' + this.bounds._southWest.lng + '|' + this.bounds._northEast.lat + ',' + this.bounds._northEast.lng]
                  ]
                }
              ]
            }
            GeoLineService.getAll({params})
              .then(response => {
                this.geoLines = []
                this.$nextTick(() => {
                  this.geoLines = response.body.data.map(x => { return {...this.setLineCoordinates(x), editable: false} })
                  this.searching.geoLines = false
                })
              })
          }
        } else {
          this.geoLines = []
          this.searching.geoLines = false
        }
        if (this.pointTypes.length > 0) {
          if (!this.searching.geoPoints) {
            this.searching.geoPoints = true
            let params = {
              includes: [
                'geo_type'
              ],
              filter_groups: [
                {
                  filters: [
                    ['geo_type_id', 'in', this.pointTypes],
                    ['boundary', 'eq', this.bounds._southWest.lat + ',' + this.bounds._southWest.lng + '|' + this.bounds._northEast.lat + ',' + this.bounds._northEast.lng]
                  ]
                }
              ]
            }
            GeoPointService.getAll({params})
              .then(response => {
                this.geoPoints = []
                this.$nextTick(() => {
                  this.geoPoints = response.body.data.map(x => { return {...this.setPointCoordinates(x), editable: false} })
                  this.searching.geoPoints = false
                })
              })
          }
        } else {
          this.geoPoints = []
          this.searching.geoPoints = false
        }
      }
    },
    setDefaultTileProvider() {
      if (this.ecosystemMaps && this.ecosystemMaps !== null && this.ecosystemMaps.defaultTileProvider && this.ecosystemMaps.defaultTileProvider[this.$options.name]) {
        let tileProvidersLocal = this.tileProvidersLocal.map(x => {
          if (x.name === this.ecosystemMaps.defaultTileProvider[this.$options.name]) {
            x.visible = true
          } else {
            x.visible = false
          }
          return x
        })
        this.$nextTick(() => {
          this.tileProvidersReady = true
        })
        return tileProvidersLocal
      }
      this.tileProvidersReady = true
    },
    fitMap() {
      this.$nextTick(() => {
        // just in case
        this.nearbyLayers = this.$refs.nearbyLayers.mapObject
        // fit bounds
        this.map.fitBounds(this.nearbyLayers.getBounds())
        this.map.invalidateSize()
      })
    },
    async emitReady() {
      this.mapHeight()
      this.map = this.$refs.map.mapObject
      this.map.invalidateSize()
      this.mapReady = true
      this.$emit('ready', true)
      this.nearbyLayers = this.$refs.nearbyLayers.mapObject
      this.computedMapHeight = this.mapHeight()
    },
    updateFenceEdited($event, index = null) {
      if (index === null) {
        this.geofence.editable = false
        this.geofence.fence = []
        this.geofence.fence = $event.layer.getLatLngs()[0].map(x => {
          return [x.lat, x.lng]
        })
        this.geofence.editable = true
      } else {
        this.geoFences[index].editable = false
        this.geoFences[index].fence = []
        this.geoFences[index].fence = $event.layer.getLatLngs()[0].map(x => {
          return [x.lat, x.lng]
        })
        this.geoFences[index].editable = true
      }
    },
    updateLine($event, index = null) {
      if (index === null) {
        this.geofence.editable = false
        this.geofence.fence = []
        this.geofence.fence = $event.layer.getLatLngs().map(x => {
          return [x.lat, x.lng]
        })
        this.geofence.editable = true
      } else {
        this.geoLines[index].editable = false
        this.geoLines[index].path = []
        this.geoLines[index].path = $event.layer.getLatLngs().map(x => {
          return [x.lat, x.lng]
        })
        this.geoLines[index].editable = true
      }
    },
    updatePoint($event, index) {
      this.geoPoints[index] = Object.assign(this.geoPoints[index], $event)
    },
    saveGeoFence($event, geofence) {
      if (geofence.id) {
        GeofenceService.update(geofence.id, geofence)
          .then(() => {
            this.$nextTick(() => {
              this.getNearbyElements()
              this.map.closePopup()
              this.editing = false
            })
          })
      }
    },
    saveGeoLine($event, line) {
      if (line.id) {
        GeoLineService.update(line.id, line)
          .then(() => {
            this.$nextTick(() => {
              this.getNearbyElements()
              this.map.closePopup()
              this.editing = false
            })
          })
      }
    },
    saveGeoPoint($event, point) {
      if (point.id) {
        GeoPointService.update(point.id, point)
          .then(() => {
            this.$nextTick(() => {
              this.getNearbyElements()
              this.map.closePopup()
              this.editing = false
            })
          })
      }
    },
    deleteGeoFence(geofence) {
      if (geofence.id) {
        if (confirm(this.$gettext('Are you sure you want to COMPLETELY DELETE THIS GEOFENCE AND ITS RELATIONS?\nWARNING: THIS ACTION CANNOT BE UNDONE!'))) {
          GeofenceService.delete(geofence.id)
            .then(() => {
              this.$nextTick(() => {
                this.getNearbyElements()
                this.map.closePopup()
                this.editing = false
              })
            })
        }
      }
    },
    deleteGeoLine(line) {
      if (line.id) {
        if (confirm(this.$gettext('Are you sure you want to COMPLETELY DELETE THIS GEOLINE AND ITS RELATIONS?\nWARNING: THIS ACTION CANNOT BE UNDONE!'))) {
          GeoLineService.delete(line.id)
            .then(() => {
              this.$nextTick(() => {
                this.getNearbyElements()
                this.map.closePopup()
                this.editing = false
              })
            })
        }
      }
    },
    deleteGeoPoint(point) {
      if (point.id) {
        if (confirm(this.$gettext('Are you sure you want to COMPLETELY DELETE THIS GEOPOINT AND ITS RELATIONS?\nWARNING: THIS ACTION CANNOT BE UNDONE!'))) {
          GeoPointService.delete(point.id)
            .then(() => {
              this.$nextTick(() => {
                this.getNearbyElements()
                this.map.closePopup()
                this.editing = false
              })
            })
        }
      }
    },
    mapHeight() {
      if (document.getElementById('map')) {
        let mapPosition = document.getElementById('map').getBoundingClientRect().top
        let windowScroll = window.scrollY
        let calc = windowScroll + mapPosition + 24
        document.getElementById('map').style.height = 'calc(100vh - ' + calc + 'px)'
        return 'calc(100vh - ' + calc + 'px)'
      }
    },
    centerElement($event) {
      this.editing = false
      let element
      if ($event.geo_type.type === 'polygon') {
        element = L.polygon(this.setFenceCoordinates($event).fence)
        this.map.fitBounds(element.getBounds())
      } else if ($event.geo_type.type === 'linestring') {
        element = L.polyline(this.setLineCoordinates($event).path)
        this.map.fitBounds(element.getBounds())
      } else {
        this.center = [$event.lat, $event.lng]
      }
    }
  }
}
</script>
